import React, { useState, useContext } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import ConfigContext from '../../../contexts/ConfigContext';
import NinModal from '../../common/modal/NinModal';
import { sendToast, formatAmDisplayString } from '../../../utils/displayUtils';
import { updateContractService } from '../../../api/actionMemoApi';
import ContractServiceUpdateForm, {
    UpdateContractServiceModalForm,
} from './ContractServiceUpdateForm';

export interface UpdateContractServiceFormBaseProps {
    title: String;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface UpdateContractServiceModalProps extends UpdateContractServiceFormBaseProps {
    amId: number;
    amNumber: number;
    supNumber: number;
    nclDepartmentCode?: string | null;
    nclDepartmentDescription?: string | null;
    nclDevCode?: string | null;
    nclDevCodeDescription?: string | null;
}

const submitUpdateContractService = async (
    config: LoruleRequestConfig,
    values: UpdateContractServiceModalForm,
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        await updateContractService(
            config,
            values.amId,
            values.nclDepartmentCode,
            values.nclDevCode,
        );
        sendToast(
            <>
                Updated Action Memo:{' '}
                <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
            </>,
        );

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Update failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const ContractServiceUpdateModal = (props: UpdateContractServiceModalProps) => {
    const config = useContext(ConfigContext);

    const [responseError, setResponseError] = useState('');

    const amUpdateValidationSchema = Yup.object().shape({
        nclDepartmentCode: Yup.string().required('NCL Department is required'),
        nclDevCode: Yup.string().required('NCL Dev Code is required'),
    });

    const initialValues = {
        amId: props.amId,
        amNumber: props.amNumber,
        supNumber: props.supNumber,
        nclDepartmentCode: props.nclDepartmentCode ? props.nclDepartmentCode : '',
        nclDepartmentDescription: props.nclDepartmentDescription
            ? props.nclDepartmentDescription
            : '',
        nclDevCode: props.nclDevCode ? props.nclDevCode : '',
        nclDevCodeDescription: props.nclDevCodeDescription ? props.nclDevCodeDescription : '',
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={amUpdateValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitUpdateContractService(
                            config,
                            values,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={(formikProps) => (
                        <ContractServiceUpdateForm
                            {...formikProps}
                            responseError={responseError}
                            setResponseError={setResponseError}
                            toggleModal={toggleModal}
                        />
                    )}
                />
            )}
        />
    );
};

export default ContractServiceUpdateModal;
